import { makeAutoObservable } from 'mobx';
import { newGuid } from 'tools/guid';
import { createModelSchemaWrapper, optionalExt } from 'framework/serializr-integration';
import { list, map, object, primitive } from 'serializr';
import React from 'react';
import { isListOperator } from 'controls/queryBuilder/utils/isListOperator';

export enum RuleDefinitionType {
	Rule = 'rule',
	Group = 'group',
}

export enum GroupConjunction {
	And = 'AND',
	Or = 'OR',
}

export class RuleProperties {
	field: string;
	operator: string;
	value: string[] | number[] = [];
	valueSrc: string[];
	valueType: string[];
	conjunction: GroupConjunction;
	not: boolean;

	constructor() {
		makeAutoObservable(this);
	}
}

type iterateRulesCallbackParameter = {
	filter: RuleDefinition;
	level: number;
	parent: RuleDefinition;
};

export class RuleDefinition {
	id: string;
	type: RuleDefinitionType;
	children1?: { [key: string]: RuleDefinition };
	properties?: RuleProperties;
	order: string[];

	constructor() {
		makeAutoObservable(this);
	}

	static emptyGroup(conjunction: GroupConjunction = GroupConjunction.And) {
		const result = new RuleDefinition();
		result.id = newGuid();
		result.type = RuleDefinitionType.Group;
		result.properties = new RuleProperties();
		result.properties.conjunction = conjunction;
		result.properties.not = false;
		result.order = [];
		result.children1 = {};
		return result;
	}

	static emptyRule(field: string, operator: string) {
		const result = new RuleDefinition();
		result.id = newGuid();
		result.type = RuleDefinitionType.Rule;
		result.properties = new RuleProperties();
		result.properties.field = field;
		result.properties.operator = operator;
		return result;
	}

	static fieldEqualRule(field: string, value: string) {
		const rule = RuleDefinition.emptyRule(field, 'equal');

		const group = RuleDefinition.emptyGroup();
		rule.properties.value = [value];
		addOrUpdateRule(group, rule);
		group.properties.conjunction = GroupConjunction.And;

		return group;
	}
}

export function addEmptyRule(parent: RuleDefinition, field: string = '', operator: string = '') {
	const rule = RuleDefinition.emptyRule(field, operator);
	addOrUpdateRule(parent, rule);
	return rule;
}

export function addOrUpdateRule(parent: RuleDefinition, child: RuleDefinition) {
	if (parent.children1[child.id] == null) {
		parent.order?.push(child.id);
	}
	parent.children1[child.id] = child;
}

export function addEmptyGroup(parent: RuleDefinition) {
	const group = RuleDefinition.emptyGroup();
	addOrUpdateRule(parent, group);
	return group;
}

export function removeRule(parent: RuleDefinition, child: string | RuleDefinition) {
	let childId: string = null;

	if (child == null) return;

	if (typeof child == 'string') {
		childId = child;
	} else {
		//sometimes id are not present so we are comparing object to search fo id
		childId = Object.keys(parent.children1).find((id) => child.id == id || parent.children1[id] == child);
	}

	if (parent.order) {
		const index = parent.order.indexOf(childId);
		parent.order.splice(index, 1);
	}
	delete parent.children1[childId];
}

export function removeInvalidRules(parent: RuleDefinition) {
	if (parent.type == RuleDefinitionType.Rule) return;

	Object.values(parent.children1).forEach((childRule) => {
		if (childRule.type == RuleDefinitionType.Rule) {
			if (!isValid(childRule)) {
				removeRule(parent, childRule);
			}
		} else {
			removeInvalidRules(childRule);
		}
	});
}

export function* iterateRules(
	parent: RuleDefinition,
	callback?: (entry: iterateRulesCallbackParameter) => boolean,
	level: number = 1,
): IterableIterator<RuleDefinition> {
	if (parent.type == RuleDefinitionType.Rule) return;

	for (const filterId of Object.keys(parent.children1)) {
		const filter = parent.children1[filterId];
		if (callback == null || callback({ filter, level, parent: parent })) yield filter;
		if (filter.type == RuleDefinitionType.Group) {
			yield* iterateRules(filter, callback, level + 1);
		}
	}
}

export function isValid(rule: RuleDefinition): boolean {
	if (rule.type == RuleDefinitionType.Rule) {
		return (
			rule.properties.field &&
			rule.properties.operator &&
			rule.properties.value?.length > 0 &&
			rule.properties.value[0] !== undefined &&
			rule.properties.value[0] !== null &&
			rule.properties.value[0] !== ''
		);
	} else {
		return Object.values(rule.children1).every((x) => isValid(x));
	}
}

export function isEmpty(rule: RuleDefinition) {
	return rule.properties.value.length == 0 || (rule.properties.value.length == 1 && !rule.properties.value[0]);
}

type DataEntry = Record<string, any>;

export class RulesHandler {
	constructor(public rules: RulesConfiguration) {}

	filter<T>(data: T[], filter: RuleDefinition) {
		return data.filter((x) => this.satisfy(x, filter));
	}

	satisfy = (entry: DataEntry, filter: RuleDefinition) => {
		if (!isValid(filter)) return true;

		if (filter.type == RuleDefinitionType.Rule) {
			return this.satisfyRule(entry, filter);
		} else {
			return this.satisfyGroup(entry, filter);
		}
	};

	satisfyRule(entry: DataEntry, filter: RuleDefinition) {
		const configuration = this.rules[filter.properties.field];
		if (!configuration) {
			console.warn('There is no configuration for field', filter.properties.field);
			return true;
		}

		switch (configuration.type) {
			case FieldType.Text: {
				const fieldValue = (entry[filter.properties.field]?.toString() ?? '').toLocaleLowerCase();
				if (isListOperator(filter.properties.operator)) {
					const listed = filter.properties.value.some((x) => x.toString().toLocaleLowerCase() == fieldValue);
					return filter.properties.operator == 'multiselect_not_equals' ? !listed : listed;
				}

				const predicate = stringPredicates.get(filter.properties.operator);
				if (predicate == null) {
					console.warn('No text predicate for operator', filter.properties.operator);
					return true;
				}

				const filterValue = (filter.properties.value[0]?.toString() ?? '').toLocaleLowerCase();
				return predicate(fieldValue, filterValue);
			}

			case FieldType.MultiSelect: {
				const filterValues = filter.properties.value as string[];
				const fieldValue: unknown = entry[filter.properties.field];
				// a field of several values is in the list while any of them is
				if (Array.isArray(fieldValue) && isListOperator(filter.properties.operator)) {
					const listed = fieldValue.some((x) => filterValues.includes(String(x)));
					return filter.properties.operator == 'multiselect_not_equals' ? !listed : listed;
				}

				const predicate = predicatesMap.get(filter.properties.operator) as GenericValueArrayPredicate;
				return predicate(fieldValue as string, filterValues);
			}
			case FieldType.Select:
			case FieldType.Boolean: {
				const equal = String(entry[filter.properties.field]) == String(filter.properties.value[0]);
				return filter.properties.operator == 'not_equal' ? !equal : equal;
			}

			case FieldType.Number:
				return compare(filter, toNumber(entry[filter.properties.field]), filter.properties.value.map(toNumber));

			case FieldType.Date:
				return compare(
					filter,
					toTimestamp(entry[filter.properties.field]),
					filter.properties.value.map(toTimestamp),
				);
		}
	}

	satisfyGroup(entry: DataEntry, filter: RuleDefinition) {
		const childKeys = Object.keys(filter.children1);
		//an empty group imposes no constraint (matches isValid()'s vacuous-true for groups) - without this,
		//an empty OR group (e.g. a search-string group built with no configured text fields to search on)
		//would wrongly reject every row instead of leaving them unfiltered
		if (childKeys.length == 0) {
			return !filter.properties.not;
		}

		let groupResult = filter.properties.conjunction != GroupConjunction.Or;

		for (const filterId of childKeys) {
			const subFilter = filter.children1[filterId];

			const ruleResult = this.satisfy(entry, subFilter);
			if (filter.properties.conjunction == GroupConjunction.Or) {
				groupResult = groupResult || ruleResult;
				if (groupResult) {
					break;
				}
			} else {
				groupResult = groupResult && ruleResult;
				if (!groupResult) {
					break;
				}
			}
		}

		return filter.properties.not ? !groupResult : groupResult;
	}
}

const stringPredicates = new Map<string, (lhv: string, rhv: string) => boolean>([
	['equal', (x: string, y: string) => x == y],
	['not_equal', (x: string, y: string) => x != y],
	['begins_with', (x: string, y: string) => x.startsWith(y)],
	['starts_with', (x: string, y: string) => x.startsWith(y)],
	['not_begins_with', (x: string, y: string) => !x.startsWith(y)],
	['contains', (x: string, y: string) => x.includes(y)],
	['not_contains', (x: string, y: string) => !x.includes(y)],
	['is_empty', (x: string) => x == ''],
	['is_not_empty', (x: string) => x != ''],
	['ends_with', (x: string, y: string) => x.endsWith(y)],
	['not_ends_with', (x: string, y: string) => !x.endsWith(y)],
	['like', (x: string, y: string) => x.includes(y)],
]);

// numbers and dates are compared as numbers; a missing one matches nothing but 'not equal'
const comparisons = new Map<string, (x: number, y: number[]) => boolean>([
	['equal', (x, [y]) => x == y],
	['not_equal', (x, [y]) => x != y],
	['less', (x, [y]) => x < y],
	['time_less', (x, [y]) => x < y],
	['less_equal', (x, [y]) => x <= y],
	['less_or_equal', (x, [y]) => x <= y],
	['greater', (x, [y]) => x > y],
	['time_greater', (x, [y]) => x > y],
	['greater_equal', (x, [y]) => x >= y],
	['greater_or_equal', (x, [y]) => x >= y],
	['between', (x, [from, to]) => x >= from && x <= to],
	['not_between', (x, [from, to]) => x < from || x > to],
]);

function compare(filter: RuleDefinition, value: number | null, filterValues: number[]) {
	const comparison = comparisons.get(filter.properties.operator);
	if (comparison == null) {
		console.warn('No comparison for operator', filter.properties.operator);
		return true;
	}

	return value == null ? filter.properties.operator == 'not_equal' : comparison(value, filterValues);
}

function toNumber(value: unknown): number | null {
	if (value == null || value === '') {
		return null;
	}

	const number = Number(value);
	return Number.isNaN(number) ? null : number;
}

// a number is taken as milliseconds already, anything else is parsed
function toTimestamp(value: unknown): number | null {
	if (value == null || value === '') {
		return null;
	}

	const time = typeof value == 'number' ? value : typeof value == 'string' ? new Date(value).getTime() : NaN;
	return Number.isNaN(time) ? null : time;
}

type GenericUnaryPredicate = <T>(x: T, y: T) => boolean;
type GenericValueArrayPredicate = <T>(x: T, y: T[]) => boolean;
type GenericArrayValuePredicate = <T>(x: T[], y: T) => boolean;
type GenericRangePredicate = <T>(x: T, y: [T, T]) => boolean;
type StringLikePredicate = (x: string, y: string[]) => boolean;

const predicatesMap = new Map<
	string,
	| GenericUnaryPredicate
	| GenericArrayValuePredicate
	| GenericValueArrayPredicate
	| GenericRangePredicate
	| StringLikePredicate
>([
	['equal', <T,>(x: T, y: T) => x == y],
	['not_equal', <T,>(x: T, y: T) => x != y],
	['in', <T,>(x: T[], y: T) => x.includes(y)],
	['not_in', <T,>(x: T[], y: T) => !x.includes(y)],
	['less', <T,>(x: T, y: T) => x < y],
	['less_or_equal', <T,>(x: T, y: T) => x <= y],
	['greater', <T,>(x: T, y: T) => x > y],
	['greater_or_equal', <T,>(x: T, y: T) => x >= y],
	['between', <T,>(x: T, y: [T, T]) => x >= y[0] && x <= y[1]],
	['not_between', <T,>(x: T, y: [T, T]) => x < y[0] || x > y[1]],

	['is_empty', <T extends Array<T1>, T1>(x: T) => x.length == 0],
	['is_not_empty', <T extends Array<T1>, T1>(x: T) => x.length > 0],
	['is_null', <T,>(x: T) => x == null],
	['is_not_null', <T,>(x: T) => x != null],
	[
		'like',
		(x: string, y: string[]) => {
			if (x.toLocaleLowerCase == null) {
				return false;
			}
			return x.toString().toLocaleLowerCase().includes(y[0].toLocaleLowerCase());
		},
	],
	['multiselect_not_equals', <T,>(x: T, y: T[]) => !y.includes(x)],
	['multiselect_equals', <T,>(x: T, y: T[]) => y.includes(x)],
	['time_greater', <T,>(x: T, y: T) => x > y],
	['time_less', <T,>(x: T, y: T) => x > y],
]);

export class RulesConfiguration {
	[index: string]: RulesConfigurationEntry;

	static buildSimpleSearchFilterConfiguration(fields: string[]) {
		const configuration = new RulesConfiguration();
		fields.forEach((field) => {
			configuration[field] = new RulesConfigurationEntry();
			configuration[field].type = FieldType.Text;
			configuration[field].fieldSettings = { allowCustomValues: true, listValues: [] };
			configuration[field].operators = [{ value: 'like', title: 'like' }];
		});
		return configuration;
	}
}

export enum FieldType {
	Text = 'text',
	MultiSelect = 'multiselect',
	Select = 'select',
	Boolean = 'boolean',
	Number = 'integer',
	Date = 'date',
}

export class RulesConfigurationEntry {
	attributes: [];
	label: string;
	type: FieldType;
	operators: {
		value: string;
		title: string;
	}[];
	fieldSettings: {
		allowCustomValues: boolean;
		listValues: {
			value: string;
			title: string;
		}[];
	};

	customMultiSelectRenderer?: (item: unknown) => React.ReactNode;
}

export function getTitleForValue(configuration: RulesConfiguration, name: string, value: string | number) {
	if (configuration[name] == undefined) return value;

	const valueEntry = configuration[name].fieldSettings.listValues.find((x) => x.value == value);
	if (valueEntry == null) return value;

	return valueEntry.title;
}

export class RuleConfigurationBuilder<DataItem> {
	private configuration: RulesConfiguration;

	private constructor(configuration?: RulesConfiguration) {
		this.configuration = configuration ?? new RulesConfiguration();
	}

	static create<DataEntry>(configuration?: RulesConfiguration) {
		return new RuleConfigurationBuilder<DataEntry>(configuration);
	}

	build() {
		return this.configuration;
	}

	addText = (key: keyof DataItem, label: string) => {
		const rule = new RulesConfigurationEntry();
		rule.label = label;
		rule.type = FieldType.Text;
		rule.attributes = [];
		rule.fieldSettings = {
			allowCustomValues: true,
			listValues: [],
		};
		rule.operators = [
			{ value: 'like', title: 'Matches' },
			{ value: 'equal', title: 'Equal' },
			{ value: 'not_equal', title: 'Not Equal' },
			{ value: 'starts_with', title: 'Begins With' },
			{ value: 'ends_with', title: 'Ends With' },
		];
		this.configuration[key as string] = rule;
		return this;
	};

	addMultiSelect = (key: keyof DataItem, label: string, listValues: { value: string; title: string }[]) => {
		const rule = new RulesConfigurationEntry();
		rule.label = label;
		rule.type = FieldType.MultiSelect;
		rule.attributes = [];
		rule.fieldSettings = {
			allowCustomValues: false,
			listValues: listValues,
		};
		rule.operators = [
			{ value: 'multiselect_equals', title: 'In' },
			{ value: 'multiselect_not_equals', title: 'Not in' },
			// { value: 'like', title: 'Matches' },
		];
		this.configuration[key as string] = rule;
		return this;
	};

	addNumber = (key: keyof DataItem, label: string) => {
		const rule = new RulesConfigurationEntry();
		rule.label = label;
		rule.type = FieldType.Number;
		rule.attributes = [];
		rule.fieldSettings = {
			allowCustomValues: true,
			listValues: [],
		};
		rule.operators = [
			{ value: 'equal', title: 'Equal' },
			{ value: 'not_equal', title: 'Not Equal' },
			{ value: 'less', title: 'Less Than' },
			{ value: 'less_equal', title: 'Less Or Equal' },
			{ value: 'greater', title: 'Greater Than' },
			{ value: 'greater_equal', title: 'Greater Or Equal' },
		];
		this.configuration[key as string] = rule;
		return this;
	};

	addDate = (key: keyof DataItem, label: string) => {
		const rule = new RulesConfigurationEntry();
		rule.label = label;
		rule.type = FieldType.Date;
		rule.attributes = [];
		rule.fieldSettings = {
			allowCustomValues: true,
			listValues: [],
		};
		rule.operators = [
			{ value: 'time_greater', title: 'Time Greater' },
			{ value: 'time_less', title: 'Time Less' },
		];
		this.configuration[key as string] = rule;
		return this;
	};

	addBool = (key: keyof DataItem, label: string) => {
		const rule = new RulesConfigurationEntry();
		rule.label = label;
		rule.type = FieldType.Boolean;
		rule.attributes = [];
		rule.fieldSettings = {
			allowCustomValues: true,
			listValues: [],
		};
		rule.operators = [
			{ value: 'equal', title: 'Equal' },
			{ value: 'not_equal', title: 'Not Equal' },
		];
		this.configuration[key as string] = rule;
		return this;
	};
}

createModelSchemaWrapper(RuleDefinition, {
	id: optionalExt(primitive()),
	type: primitive(),
	children1: optionalExt(map(object(RuleDefinition))),
	properties: optionalExt(object(RuleProperties)),
	order: list(primitive()),
});

createModelSchemaWrapper(RuleProperties, {
	field: primitive(),
	operator: primitive(),
	value: list(primitive()),
	valueSrc: list(primitive()),
	valueType: list(primitive()),
	conjunction: primitive(),
	not: primitive(),
});
